import React, { useState, useEffect, useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  calculateChartData,
  getSelectedCategoryName,
} from "../helpers/statsCalculations";
import "../styles/AssetActivityChart.scss";

interface AssetActivityChartProps {
  assets: any[];
  assetCategories: any[];
  selectedCategoryId?: string | null;
  onCategorySelect?: any;
  onClearFilter?: any;
  categoryFilter?: any;
}

const AssetActivityChart: React.FC<AssetActivityChartProps> = ({
  assets,
  assetCategories,
  selectedCategoryId = null,
  onCategorySelect,
  onClearFilter,
  categoryFilter,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const chartData = useMemo(() => {
    return calculateChartData(assets, assetCategories, selectedCategoryId);
  }, [assets, assetCategories, selectedCategoryId]);

  const selectedCategoryName = useMemo(() => {
    return getSelectedCategoryName(assetCategories, selectedCategoryId);
  }, [assetCategories, selectedCategoryId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategoryId]);

  const totalPages = Math.ceil(chartData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = chartData.slice(startIndex, startIndex + itemsPerPage);

  // Handle category selection (used by both X-axis and data points)
  const handleCategorySelect = (categoryName: string) => {
    if (onCategorySelect) {
      const dataPoint = currentData.find((d) => d.name === categoryName);
      if (dataPoint) {
        onCategorySelect(dataPoint.categoryId, true);
      }
    }
  };

  const CustomXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const text = payload.value;
    const maxLength = 15;
    const maxLines = 3;

    const lines = [];

    if (text.length > maxLength) {
      const words = text.split(" ");

      if (words.length > 1) {
        let currentLine = "";

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;

          if (testLine.length <= maxLength) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              if (lines.length >= maxLines - 1) {
                const remainingWords = words.slice(words.indexOf(word));
                const remainingText = remainingWords.join(" ");
                if (remainingText.length > maxLength) {
                  lines.push(remainingText.substring(0, maxLength - 3) + "...");
                } else {
                  lines.push(remainingText);
                }
                break;
              }
              currentLine = word;
            } else {
              if (word.length > maxLength) {
                lines.push(word.substring(0, maxLength - 3) + "...");
                break;
              } else {
                currentLine = word;
              }
            }
          }
        }

        if (currentLine && lines.length < maxLines) {
          lines.push(currentLine);
        }
      } else {
        const word = text;
        if (word.length > maxLength * maxLines) {
          lines.push(word.substring(0, maxLength - 3) + "...");
        } else if (word.length > maxLength * 2) {
          const partLength = Math.ceil(word.length / 3);
          lines.push(word.substring(0, partLength));
          lines.push(word.substring(partLength, partLength * 2));
          lines.push(word.substring(partLength * 2));
        } else if (word.length > maxLength) {
          const mid = Math.ceil(word.length / 2);
          lines.push(word.substring(0, mid));
          lines.push(word.substring(mid));
        } else {
          lines.push(word);
        }
      }
    } else {
      lines.push(text);
    }

    return (
      <g transform={`translate(${x},${y})`}>
        {lines.map((line, i) => (
          <text
            key={i}
            x={0}
            y={i * 14}
            fill="#6b7280"
            textAnchor="middle"
            fontSize={11}
            dominantBaseline="hanging"
            style={{ cursor: onCategorySelect ? "pointer" : "default" }}
            onClick={() => {
              if (onCategorySelect) {
                handleCategorySelect(text);
              }
            }}>
            {line}
          </text>
        ))}
      </g>
    );
  };

  // Custom dot click handler
  const handleDotClick = (data: any) => {
    if (onCategorySelect) {
      onCategorySelect(data.categoryId, false);
    }
  };

  // Custom Dot Component with click functionality
  const CustomDot = (props: any) => {
    const { cx, cy, stroke, fill, payload } = props;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={fill}
        stroke={stroke}
        strokeWidth={2}
        style={{ cursor: onCategorySelect ? "pointer" : "default" }}
        onClick={() => handleDotClick(payload)}
      />
    );
  };

  // Custom Active Dot with enhanced click area
  const CustomActiveDot = (props: any) => {
    const { cx, cy, stroke, fill, payload } = props;
    return (
      <g>
        {/* Invisible larger click area */}
        <circle
          cx={cx}
          cy={cy}
          r={12}
          fill="transparent"
          style={{ cursor: onCategorySelect ? "pointer" : "default" }}
          onClick={() => handleDotClick(payload)}
        />
        {/* Visible dot */}
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={fill}
          stroke={stroke}
          strokeWidth={3}
          style={{ cursor: onCategorySelect ? "pointer" : "default" }}
          onClick={() => handleDotClick(payload)}
        />
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const assignedCount =
        payload.find((p: any) => p.dataKey === "assigned")?.value || 0;
      const inStockCount =
        payload.find((p: any) => p.dataKey === "inStock")?.value || 0;
      const inActiveCount =
        payload.find((p: any) => p.dataKey === "inActive")?.value || 0;
      const total = payload.find((p: any) => p.dataKey === "total")?.value || 0;

      return (
        <div className="custom-tooltip">
          <p className="tooltip-title">{label}</p>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot assigned" />
              <span>Assigned</span>
            </div>
            <span className="tooltip-value">{assignedCount}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot in-stock" />
              <span>In Stock</span>
            </div>
            <span className="tooltip-value">{inStockCount}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot in-active" />
              <span>Inactive</span>
            </div>
            <span className="tooltip-value total">{inActiveCount}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot total" />
              <span>Total</span>
            </div>
            <span className="tooltip-value total">{total}</span>
          </div>
          {onCategorySelect && (
            <div className="tooltip-footer">
              <small className="text-muted">
                <i className="ti ti-click me-1"></i>
                Click labels or data points to drill down
              </small>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  if (chartData.length === 0) {
    return (
      <div className="asset-chart-container">
        <div className="chart-header">
          <p className="chart-subtitle">{selectedCategoryName} Breakdown</p>
        </div>
        <div className="alert alert-info">
          <i className="ti ti-info-circle me-2"></i>
          No subcategories available for the selected category.
        </div>
      </div>
    );
  }

  return (
    <div className="asset-chart-container">
      <div className="chart-header">
        <div className="chart-title-section">
          <p className="chart-subtitle">{selectedCategoryName} Breakdown</p>
          {onCategorySelect && (
            <small className="text-muted">
              <i className="ti ti-click me-1"></i>
              Click on labels or data points to drill down further
            </small>
          )}
        </div>
        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-dot assigned" />
            <span className="legend-text">Assigned</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot in-stock" />
            <span className="legend-text">In Stock</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot in-active" />
            <span className="legend-text">Inactive</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot total" />
            <span className="legend-text">Total Assets</span>
          </div>
        </div>
      </div>

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={currentData}
            margin={{ top: 10, right: 60, left: 60, bottom: 0 }}>
            <CartesianGrid
              strokeDasharray="2 4"
              stroke="#d1d5db"
              horizontal={true}
              vertical={true}
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={true}
              tick={<CustomXAxisTick />}
              height={50}
              interval={0}
            />
            <YAxis
              axisLine={false}
              tickLine={true}
              tick={{ fontSize: 13, fill: "#6b7280" }}
              width={40}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{
                stroke: "#d1d5db",
                strokeWidth: 1,
                strokeDasharray: "4 4",
              }}
            />
            <Area
              type="monotone"
              dataKey="inStock"
              fill="rgba(139, 92, 246, 0.1)"
              stroke="none"
            />
            <Area
              type="monotone"
              dataKey="assigned"
              fill="rgba(59, 130, 246, 0.1)"
              stroke="none"
            />
            <Line
              type="monotone"
              dataKey="assigned"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<CustomActiveDot />}
            />
            <Line
              type="monotone"
              dataKey="inStock"
              stroke="#38b000"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<CustomActiveDot />}
            />
            <Line
              type="monotone"
              dataKey="inActive"
              stroke="#ef4444"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<CustomActiveDot />}
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<CustomActiveDot />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="d-flex align-items-center justify-content-center gap-5">
        <span className="text-muted">
          Showing {startIndex + 1}-
          {Math.min(startIndex + itemsPerPage, chartData.length)} of{" "}
          {chartData.length} subcategories
        </span>
        <div className="d-flex gap-3 align-items-center">
          <button
            className="btn btn-info btn-sm"
            onClick={handlePrevPage}
            disabled={currentPage === 1}>
            Prev
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}>
            Next
          </button>
          <button
            className="btn btn-success btn-sm"
            onClick={onClearFilter}
            disabled={!categoryFilter}>
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetActivityChart;
